import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';
import _ from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useBookingService } from '@bingads-webui-campaign-react/smart-page-data-service';
import {
  validateEmailAddress,
  validatePhone,
  sendAnalyticsData,
  sendLeadsTrackingData,
} from '@bingads-webui-campaign-react/smart-component-util';
import { useCurrentLocale } from '@bingads-webui-campaign-react/smart-page-context';
import { validateText } from '@bingads-webui-campaign-react/smart-component-text';

import { getDurationOptions, messageMaxCount } from '../utils/booking-config';
import {
  getTimeOptions,
  getTimeInMinutes,
  getTimeString,
} from '../utils/get-available-timeslots';
import { SmartBookingModal } from './booking-modal-base';
import { BookingStepTime } from './booking-step-time';
import { BookingStepUserInfo } from './booking-step-user-info';

const nameMaxLen = 50;

const getStyles = () => ({
  container: {
    width: '100%',
    position: 'relative',
  },
  step: {
    width: '100%',
    padding: '20px 70px',
  },
  stepMobile: {
    padding: '20px 28px',
  },
});

const customModalStyle = {
  main: {
    width: '590px',
    height: '680px',
  },
};

const getMobileModalStyle = isRuntime => ({
  main: {
    width: isRuntime ? '90vw' : '360px',
  },
});

const STEP_CONFIG = {
  ONE: {
    key: 'ONE',
    buttonText: _TL_('Appointment details'),
    validator: ({ time }, i18n) => {
      if (!time) {
        return i18n.getString(_TL_('Please select specific time'));
      }

      return null;
    },
  },
  TWO: {
    key: 'TWO',
    buttonText: _TL_('Submit'),
    validator: ({
      email, phoneNumber, extraMessage, name,
    }, i18n) => {
      const emailError = validateEmailAddress(email) ? '' : i18n.getString(_TL_('Invalid email address.'));
      const phoneError = validatePhone(phoneNumber) ? '' : i18n.getString(_TL_('Invalid phone number.'));
      const firstNameError = validateText(name.firstName, nameMaxLen, i18n).errorMessage;
      const lastNameError = validateText(name.lastName, nameMaxLen, i18n).errorMessage;
      const extraMessageError = validateText(extraMessage, messageMaxCount, i18n).errorMessage;

      if (!emailError && !phoneError && !extraMessageError && !firstNameError && !lastNameError) {
        return null;
      }

      return {
        email: emailError,
        phoneNumber: phoneError,
        extraMessage: extraMessageError,
        name: { firstName: firstNameError, lastName: lastNameError },
      };
    },
  },
};
const dateOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const UstyledBookingSteps = ({
  classes,
  i18n,
  isOpen,
  bookingProps,
  onClose,
  logger,
  isRuntime,
  isMobile,
  userLocale,
}) => {
  const {
    smartPageId,
    availableBookingHours,
    email: customerEmail,
    landingLink,
    businessName,
    customerTimeZoneOffset,
  } = bookingProps;
  const durationOptions = useMemo(() => getDurationOptions(i18n), [i18n]);

  const {
    // create
    createBooking,
    isCreatingBooking,
    // get
    isGetExistingBookings,
    existingBookings,
    getExistingBooking,
  } = useBookingService({ logger });
  const locale = useCurrentLocale();

  const [currentStep, setCurrentStep] = useState(STEP_CONFIG.ONE);
  // Step one:
  const [duration, setDuration] = useState(durationOptions[0]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({});
  const [stepOneError, setStepOneError] = useState(null);
  const [availability, setAvailability] = useState([]);
  // Step two:
  const [name, setName] = useState({ firstName: '', lastName: '', error: { firstName: '', lastName: '' } });
  const [email, setEmail] = useState({ value: '', error: '' });
  const [phoneNumber, setPhoneNumber] = useState({ value: '', error: '' });
  const [extraMessage, setExtraMessage] = useState({ value: '', error: '' });
  const [stepTwoError, setStepTwoError] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);

  const formattedBookingInfo = `${selectedDate.toLocaleDateString(locale, dateOptions)}, ${duration.text}, ${selectedTime.text}`;

  const onSelectDate = useCallback((date) => {
    setSelectedDate(date);
    // Clear the selected time
    setSelectedTime({});
  }, []);

  const onSelectDuration = useCallback((e, item) => {
    setDuration(item);
    // Clear the selected time
    setSelectedTime({});
  }, []);

  const onSelectTime = useCallback((time) => {
    setSelectedTime(time);
    setStepOneError(null);
  }, []);

  const onChangeName = useCallback((e, newValue, key) => {
    setName(prev => ({
      ...prev,
      [key]: newValue,
      error: {
        ...prev.error,
        [key]: validateText(newValue, nameMaxLen, i18n).errorMessage,
      },
    }));
  }, [i18n]);

  const onChangeEmail = useCallback((e, newValue) => {
    setEmail({
      value: newValue,
      error: validateEmailAddress(newValue)
        ? ''
        : i18n.getString(_TL_('Invalid email address.')),
    });
  }, [i18n]);

  const onChangePhoneNumber = useCallback((e, newValue) => {
    setPhoneNumber({
      value: newValue,
      error: validatePhone(newValue)
        ? ''
        : i18n.getString(_TL_('Invalid phone number.')),
    });
  }, [i18n]);

  const onChangeExtraMessage = useCallback((e, newValue) => {
    setExtraMessage({
      value: newValue,
      error: validateText(newValue, messageMaxCount, i18n).errorMessage,
    });
  }, [i18n]);

  const onClickFooterBtn = () => {
    if (currentStep.key === STEP_CONFIG.ONE.key) {
      const errors = currentStep.validator({ time: selectedTime.text }, i18n);
      setStepOneError(errors);

      if (!errors) {
        setCurrentStep(STEP_CONFIG.TWO);
      }
    } else {
      const errors = currentStep.validator({
        email: email.value,
        phoneNumber: phoneNumber.value,
        extraMessage: extraMessage.value,
        name: { firstName: name.firstName, lastName: name.lastName },
      }, i18n);

      if (!errors) {
        if (!isRuntime) {
          setShowWarning(true);
          return;
        }

        // Trigger only in Runtime
        sendAnalyticsData('BookAppointment');
        sendLeadsTrackingData();

        // Create booking
        const timeString = getTimeString(
          selectedDate,
          selectedTime.key,
          moment().utcOffset()
        );

        createBooking({
          smartPageId,
          customerEmailAddress: customerEmail,
          manageAppointmentLink: landingLink,
          bookingDetails: {
            Name: `${name.firstName} ${name.lastName}`,
            PhoneNumber: phoneNumber.value,
            EmailAddress: email.value,
            Service: '',
            Time: timeString,
            TimeInMinutes: getTimeInMinutes(duration.key),
            Message: extraMessage.value,
            UserTimeZoneOffset: moment().utcOffset(),
          },
          businessName,
          customerTimeZoneOffset,
          userLocale,
        }).then(() => {
          logger.logTrace(`CTA|submitBooking|${window.location.hostname}|BookAppointment`);

          onClose({}, true);
        }).catch((error) => {
          setStepTwoError(error);
        });
      } else {
        setEmail(prev => ({ ...prev, error: errors.email }));
        setPhoneNumber(prev => ({ ...prev, error: errors.phoneNumber }));
        setExtraMessage(prev => ({ ...prev, error: errors.extraMessage }));
        setName(prev => ({ ...prev, error: errors.name }));
      }
    }
  };

  const onBackToStepOne = useCallback(() => {
    setStepTwoError('');
    setCurrentStep(STEP_CONFIG.ONE);
    setShowWarning(false);
    setAutoFocus(true);
  }, []);

  useEffect(() => {
    // Always fetch data when isOpen is true, as user might book one and want to book another again,
    // we have to make sure the specific timeslot is occupied.
    // Only fetch data in Runtime
    if (isOpen && isRuntime) {
      getExistingBooking({ smartPageId });
    }
  }, [getExistingBooking, isOpen, smartPageId, isRuntime]);

  // Recalculate the availability when duration, date or existingBookings change.
  useEffect(() => {
    if (isOpen) {
      const userTimeZoneOffset = moment().utcOffset();
      const availableStartTime = getTimeOptions(
        availableBookingHours,
        existingBookings,
        duration.key,
        selectedDate,
        customerTimeZoneOffset,
        userTimeZoneOffset
      );

      setAvailability(availableStartTime);
    }
  }, [
    availableBookingHours, duration.key, existingBookings,
    selectedDate, isOpen, customerTimeZoneOffset,
  ]);

  return (
    <SmartBookingModal
      i18n={i18n}
      isOpen={isOpen}
      isLoading={isCreatingBooking}
      title={i18n.getString(_TL_('Book appointment'))}
      actionButtonLabel={i18n.getString(currentStep.buttonText)}
      onClickFooterButton={onClickFooterBtn}
      onCloseModal={onClose}
      modalStyle={isMobile ? getMobileModalStyle(isRuntime) : customModalStyle}
    >
      <div className={classNames(classes.container, 'booking-steps-container')}>
        {
          currentStep.key === STEP_CONFIG.ONE.key
            ? (
              <BookingStepTime
                i18n={i18n}
                className={isMobile ? classes.stepMobile : classes.step}
                isLoading={isGetExistingBookings}
                availability={availability}
                durationOptions={durationOptions}
                selectedDuration={duration}
                onSelectDuration={onSelectDuration}
                selectedDate={selectedDate}
                onSelectDate={onSelectDate}
                selectedTime={selectedTime}
                onSelectTime={onSelectTime}
                errorMessage={stepOneError}
                isMobile={isMobile}
                isInPreview={!isRuntime}
                focus={autoFocus}
              />
            ) : (
              <BookingStepUserInfo
                i18n={i18n}
                bookingTimeInfo={formattedBookingInfo}
                onEditTime={onBackToStepOne}
                name={name}
                onChangeName={onChangeName}
                email={email}
                onChangeEmail={onChangeEmail}
                phoneNumber={phoneNumber}
                onChangePhoneNumber={onChangePhoneNumber}
                extraMessage={extraMessage}
                errorMessage={stepTwoError}
                onChangeExtraMessage={onChangeExtraMessage}
                showWarning={showWarning}
                isMobile={isMobile}
              />
            )
        }
      </div>
    </SmartBookingModal>
  );
};

UstyledBookingSteps.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    step: PropTypes.string.isRequired,
    stepMobile: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  bookingProps: PropTypes.shape({
    smartPageId: PropTypes.string,
    availableBookingHours: PropTypes.arrayOf(PropTypes.shape({
      day: PropTypes.string,
      isOpenAllDay: PropTypes.bool,
      timeSlots: PropTypes.arrayOf(PropTypes.shape({})),
      isValid: PropTypes.bool,
    })),
    email: PropTypes.string,
    landingLink: PropTypes.string,
    businessName: PropTypes.string,
    customerTimeZoneOffset: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
    logTrace: PropTypes.func,
  }),
  isRuntime: PropTypes.bool,
  isMobile: PropTypes.bool,
  userLocale: PropTypes.string.isRequired,
};

UstyledBookingSteps.defaultProps = {
  logger: {
    logError: _.noop,
    logTrace: _.noop,
  },
  isRuntime: true,
  isMobile: false,
};

export const BookingSteps = React.memo(withDefaultStyles(
  UstyledBookingSteps,
  getStyles
));
